"use client";

import { useEffect, useRef } from "react";

const CONFIG = {
  gridCount: 24,
  minSpacing: 40,
  maxSpacing: 100,
  lineWidth: 0.3,
  dotSize: 0.8,
  dotScaleMultiplier: 0.8,
  perspective: 800,
  waveSpeed: 0.015,
  waveHeight: 30,
  waveFrequency: 0.005,
  mouseInfluence: 300,
  mouseWaveStrength: 60,
  mouseSmoothFactor: 0.08,
  wobbleAmount: 4,
  wobbleFrequency: 0.005,
};

const HEIGHT_MULTIPLIER = 1.5;

interface GridPoint {
  x: number;
  y: number;
  z: number;
  baseX: number;
  baseY: number;
}

export function GridWaveBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const smoothMouseRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const timeRef = useRef<number>(0);
  const gridRef = useRef<GridPoint[][]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { gridCount, minSpacing, maxSpacing, lineWidth, dotSize, dotScaleMultiplier, perspective, waveSpeed, waveHeight, waveFrequency, mouseInfluence, mouseWaveStrength, mouseSmoothFactor, wobbleAmount, wobbleFrequency } = CONFIG;

    // 패럴랙스 업데이트
    const updateParallax = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = docHeight > 0 ? scrollY / docHeight : 0;
      const maxOffset = window.innerHeight * (HEIGHT_MULTIPLIER - 1);
      const offset = -scrollProgress * maxOffset;
      container.style.transform = `translateY(${offset}px)`;
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * HEIGHT_MULTIPLIER;
      initGrid();
    };

    const initGrid = () => {
      const calculatedSpacing = Math.min(maxSpacing, Math.max(minSpacing, canvas.width / gridCount));
      
      const cols = Math.ceil(canvas.width / calculatedSpacing) + 4;
      const rows = Math.ceil(canvas.height / calculatedSpacing) + 4;
      const offsetX = (canvas.width - (cols - 1) * calculatedSpacing) / 2;
      const offsetY = (canvas.height - (rows - 1) * calculatedSpacing) / 2;

      gridRef.current = [];
      for (let row = 0; row < rows; row++) {
        const rowPoints: GridPoint[] = [];
        for (let col = 0; col < cols; col++) {
          rowPoints.push({
            x: offsetX + col * calculatedSpacing,
            y: offsetY + row * calculatedSpacing,
            z: 0,
            baseX: offsetX + col * calculatedSpacing,
            baseY: offsetY + row * calculatedSpacing,
          });
        }
        gridRef.current.push(rowPoints);
      }
    };

    const project = (point: GridPoint): { x: number; y: number; scale: number } => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      const scale = perspective / (perspective + point.z);
      const x = centerX + (point.x - centerX) * scale;
      const y = centerY + (point.y - centerY) * scale;

      return { x, y, scale };
    };

    const updateGrid = () => {
      const time = timeRef.current;
      const grid = gridRef.current;
      
      smoothMouseRef.current.x += (mouseRef.current.x - smoothMouseRef.current.x) * mouseSmoothFactor;
      smoothMouseRef.current.y += (mouseRef.current.y - smoothMouseRef.current.y) * mouseSmoothFactor;
      
      const mx = smoothMouseRef.current.x;
      const my = smoothMouseRef.current.y;

      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
          const point = grid[row][col];

          const waveX = Math.sin(point.baseX * waveFrequency + time * waveSpeed) * waveHeight;
          const waveY = Math.cos(point.baseY * waveFrequency + time * waveSpeed * 0.7) * waveHeight;
          const waveXY = Math.sin((point.baseX + point.baseY) * waveFrequency * 0.5 + time * waveSpeed * 0.5) * waveHeight * 0.5;

          const dx = point.baseX - mx;
          const dy = point.baseY - my;
          const distance = Math.sqrt(dx * dx + dy * dy);

          let mouseWave = 0;
          if (distance < mouseInfluence) {
            const influence = Math.pow(1 - distance / mouseInfluence, 2);
            mouseWave = Math.sin(distance * 0.01 - time * 0.03) * mouseWaveStrength * influence;
          }

          point.z = waveX + waveY + waveXY + mouseWave;

          point.x = point.baseX + Math.sin(time * waveSpeed * 0.5 + point.baseY * wobbleFrequency) * wobbleAmount;
          point.y = point.baseY + Math.cos(time * waveSpeed * 0.5 + point.baseX * wobbleFrequency) * wobbleAmount;
        }
      }
    };

    const drawGrid = () => {
      const grid = gridRef.current;
      ctx.lineWidth = lineWidth;

      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
          const point = grid[row][col];
          const projected = project(point);

          const normalizedZ = (point.z + waveHeight * 2) / (waveHeight * 4);
          // Detect dark mode from document
          const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
          const hue = isDark ? 260 + normalizedZ * 30 : 250 + normalizedZ * 20;
          const lightness = isDark ? 35 + normalizedZ * 20 : 60 + normalizedZ * 15;
          const alpha = isDark ? 0.15 + normalizedZ * 0.25 : 0.08 + normalizedZ * 0.15;

          if (col < grid[row].length - 1) {
            const nextPoint = grid[row][col + 1];
            const nextProjected = project(nextPoint);

            ctx.beginPath();
            ctx.strokeStyle = `hsla(${hue}, 70%, ${lightness}%, ${alpha})`;
            ctx.moveTo(projected.x, projected.y);
            ctx.lineTo(nextProjected.x, nextProjected.y);
            ctx.stroke();
          }

          if (row < grid.length - 1) {
            const nextPoint = grid[row + 1][col];
            const nextProjected = project(nextPoint);

            ctx.beginPath();
            ctx.strokeStyle = `hsla(${hue}, 70%, ${lightness}%, ${alpha})`;
            ctx.moveTo(projected.x, projected.y);
            ctx.lineTo(nextProjected.x, nextProjected.y);
            ctx.stroke();
          }

          const currentDotSize = dotSize + projected.scale * dotScaleMultiplier;
          ctx.beginPath();
          ctx.fillStyle = `hsla(${hue}, 80%, ${lightness + 20}%, ${alpha + 0.1})`;
          ctx.arc(projected.x, projected.y, currentDotSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      timeRef.current += 1;
      updateGrid();
      drawGrid();

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current = { x: e.clientX, y: e.clientY - rect.top };
    };

    const handleClick = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current = { x: e.clientX, y: e.clientY - rect.top };
    };

    const handleScroll = () => {
      updateParallax();
    };

    resizeCanvas();
    updateParallax();
    animate();

    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-x-0 top-0 -z-10 will-change-transform"
      style={{ height: `${100 * HEIGHT_MULTIPLIER}vh` }}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full bg-transparent"
        style={{ pointerEvents: "auto", cursor: "pointer" }}
      />
    </div>
  );
}
